import { useState } from 'react';
import PersonInfo from './PersonCard/PersonInfo/PersonInfo';
import ErrorState from 'common/ErrorState/ErrorState';
import PersonCard from './PersonCard/PersonCard';
import { useRevealGroup } from 'hooks/useReveal';
import { useContent } from 'hooks/useContent';
import Loader from 'components/Loader/Loader';
import './Management.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Керівництво — ADR-0005, ADR-0008.
 *
 * Було: перемикач типу порівнював innerText кнопки з рядком «Рада СГОСТУ»;
 * картки всіх людей рендерилися двічі (сітка + прихована деталь на кожного).
 * Стало: явний стан вкладок, одне модальне вікно на вибрану людину.
 */
export default function Management() {
  const language = useLanguage();
  const [managementType, setManagementType] = useState('council');
  const [activePersonId, setActivePersonId] = useState(null);
  const { data: management, status, error, retry, isLoading } = useContent('management');
  const gridRef = useRevealGroup('.management-card-wrapper', { step: 40 }, `${status}-${managementType}`);

  const people = [...management]
    .sort((a, b) => {
      const placeA = a.place;
      const placeB = b.place;
      return placeA && placeB ? placeA - placeB : placeA ? -1 : placeB ? 1 : 0;
    })
    .filter(({ council, regions }) => (managementType === 'council' ? council : regions));

  const activePerson = management.find(({ id }) => id === activePersonId);

  const tabs = [
    { id: 'council', ua: 'Рада СГОСТУ', en: 'The Council of SGOSTU' },
    { id: 'regions', ua: 'Обласні відокремлені підрозділи', en: 'Regional separate subdivisions' },
  ];

  return (
    <div className='management section'>
      <div className='container'>
        <header className='management-head'>
          <h1 className='page-title'>{language === 'en' ? 'Management' : 'Керівництво'}</h1>
        </header>

        {isLoading && <Loader />}

        {status === 'failed' && <ErrorState error={error} onRetry={retry} />}

        {status === 'succeeded' && (
          <>
            <div className='managements-selection' role='tablist'>
              {tabs.map((tab) => (
                <button
                  type='button'
                  role='tab'
                  key={tab.id}
                  aria-selected={managementType === tab.id}
                  className={`management-type ${managementType === tab.id ? 'is-active' : ''}`}
                  onClick={() => {
                    setManagementType(tab.id);
                    setActivePersonId(null);
                  }}
                >
                  {language === 'en' ? tab.en : tab.ua}
                </button>
              ))}
            </div>

            {people.length > 0 ? (
              <div className='management-cards' ref={gridRef}>
                {people.map((person) => (
                  <PersonCard
                    key={person.id}
                    personId={person.id}
                    person={person}
                    managementType={managementType}
                    onOpen={setActivePersonId}
                  />
                ))}
              </div>
            ) : (
              <p className='empty-note'>{language === 'en' ? 'The list is empty.' : 'Список порожній.'}</p>
            )}
          </>
        )}
      </div>

      {activePerson && (
        <PersonInfo
          person={activePerson}
          managementType={managementType}
          onClose={() => setActivePersonId(null)}
        />
      )}
    </div>
  );
}
